import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import { cn } from "../../lib/utils";

function DropdownMenu(props) {
  return <DropdownMenuPrimitive.Root {...props} />;
}

function DropdownMenuTrigger(props) {
  return <DropdownMenuPrimitive.Trigger {...props} />;
}

function DropdownMenuGroup(props) {
  return <DropdownMenuPrimitive.Group {...props} />;
}

function DropdownMenuPortal(props) {
  return <DropdownMenuPrimitive.Portal {...props} />;
}

function DropdownMenuSub(props) {
  return <DropdownMenuPrimitive.Sub {...props} />;
}

const DropdownMenuContent = React.forwardRef(
  ({ className, sideOffset = 4, style, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "tw-z-50 tw-min-w-[180px] tw-overflow-y-auto tw-rounded-lg tw-border tw-border-white/[0.08] tw-bg-surface-secondary tw-p-1.5 tw-text-white tw-shadow-xl",
          "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 data-[state=open]:tw-zoom-in-95",
          "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95",
          "member-scroll",
          className
        )}
        style={{
          zIndex: 50,
          minWidth: 180,
          // Radix exposes the space actually available before the viewport
          // edge via this custom property — without capping to it (and
          // switching hidden -> auto) the menu just renders past the edge
          // with no way to reach the rest, which is what "scroll doesn't
          // work" was: overflow was hidden and nothing bounded the height.
          maxHeight: "var(--radix-dropdown-menu-content-available-height, 320px)",
          overflowY: "auto",
          overflowX: "hidden",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "#1e1e1e",
          color: "#fff",
          padding: 6,
          boxShadow: "0 20px 40px -14px rgba(0,0,0,0.7)",
          ...style,
        }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
);
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = React.forwardRef(
  ({ className, style, active = false, ...props }, ref) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        "tw-relative tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-outline-none tw-transition-colors",
        "focus:tw-bg-white/[0.06] focus:tw-text-white",
        "data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50",
        className
      )}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 12.5,
        fontWeight: 500,
        color: active ? "#fff" : "#c3c8cc",
        background: active ? "rgba(0,173,181,0.16)" : "transparent",
        outline: "none",
        transition: "background 0.15s, color 0.15s",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = active
          ? "rgba(0,173,181,0.24)"
          : "rgba(255,255,255,0.06)";
        e.currentTarget.style.color = "#fff";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = active ? "rgba(0,173,181,0.16)" : "transparent";
        e.currentTarget.style.color = active ? "#fff" : "#c3c8cc";
      }}
      {...props}
    />
  )
);
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuLabel = React.forwardRef(
  ({ className, style, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        "tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-muted",
        className
      )}
      style={{
        padding: "8px 12px",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.04em",
        color: "#c3c8cc",
        ...style,
      }}
      {...props}
    />
  )
);
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = React.forwardRef(
  ({ className, style, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn("tw-mx-1.5 tw-my-1 tw-h-px tw-bg-white/[0.06]", className)}
      style={{ margin: "4px 6px", height: 1, background: "rgba(255,255,255,0.06)", ...style }}
      {...props}
    />
  )
);
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuCheckboxItem = React.forwardRef(
  ({ className, children, checked, style, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      checked={checked}
      className={cn(
        "tw-relative tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-py-2 tw-pl-8 tw-pr-3 tw-text-sm tw-font-medium tw-outline-none tw-transition-colors",
        "focus:tw-bg-white/[0.06] focus:tw-text-white",
        "data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50",
        className
      )}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        borderRadius: 6,
        padding: "8px 12px 8px 30px",
        fontSize: 12.5,
        fontWeight: 500,
        color: "#c3c8cc",
        outline: "none",
        transition: "background 0.15s, color 0.15s",
        ...style,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      {...props}
    >
      <span
        style={{
          position: "absolute",
          left: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 15,
          height: 15,
          borderRadius: 4,
          border: checked ? "1px solid #00ADB5" : "1px solid rgba(255,255,255,0.25)",
          background: checked ? "rgba(0,173,181,0.15)" : "transparent",
        }}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <Check size={11} color="#00ADB5" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
);
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
};
